import { Injectable } from '@angular/core';
import { Tool } from "@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript";
import { MapPanAndZoomStream } from "src/app/feature/map-viewer/common/map-pan-zoom-stream.service";
import { MapService } from "src/app/feature/map-viewer/map.service";
import { LayersStore } from "../../current-layers/layers-store.service";
import { LayersStreams } from "../../current-layers/layers-streams.service";

@Injectable({ providedIn: 'root' })
export class ZoomToLayerTool {
    constructor(
        private layersStore: LayersStore,
        private mapPanAndZoomStream: MapPanAndZoomStream,
        private mapService: MapService,
        private layersStreams: LayersStreams,
    ) {}
    
    readonly definition: Tool = {
        name: 'zoom_to_layer',
        description: 'Zoom the map to show the full extent of a specific layer. Use this when the user asks to zoom to, focus on, or show a particular layer on the map.',
        parameters: {
            type: 'object',
            properties: {
                layer_id: {
                    type: 'string',
                    description: 'The ID of the layer to zoom to'
                }
            },
            required: ['layer_id']
        }
    };

    /**
     * Zooms the map to show the full extent of the specified layer.
     * If the layer is not currently visible, it will be made visible first.
     */
    async execute(params: { layer_id: string }): Promise<string> {
        try {
            const layer = await this.layersStore.getMapWorkspaceLayerByIdPromise(params.layer_id);
            if (!layer) {
                return JSON.stringify({ status: 'error', message: 'Layer not found' });
            }

            // Check if layer is visible, if not make it visible
            const wasHidden = !layer.visible;
            if (wasHidden) {
                // Add layer to visible layers
                const currentVisibleLayers = this.layersStreams.visibleLayersStream.getValue();
                layer.visible = true;
                this.layersStreams.setVisibleLayers([...currentVisibleLayers, layer], true);
            }

            const bounds = await this.layersStore.loadLayersBounds([layer]);
            if (!bounds || !bounds.isValid()) {
                return JSON.stringify({ status: 'error', message: 'Could not determine layer bounds' });
            }

            const basemapMode = this.mapService.selectedBaseMapMode;
            this.mapPanAndZoomStream.zoomToBounds(bounds, [360, 60], [340, 10], {
                maxZoom: basemapMode?.maxZoom
            });

            // Defer visibility to ensure layer content is loaded on the map
            this.layersStreams.deferVisibleLayersStream.next([layer]);

            return JSON.stringify({
                status: 'ok',
                layerName: layer.layerName,
                madeVisible: wasHidden 
            });
        } catch (error) {
            return JSON.stringify({ status: 'error', message: 'Failed to zoom to layer' });
        }
    }
}